import FileInputUploadFieldNew from 'components/styledFormElements/FineInputUploadNew'
import InputField from 'components/styledFormElements/InputField'
import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useObjectUrl } from 'hooks/useObjectUrl'
import { getImageSrc } from 'services/util'

type Prefix = 'landing_page_one' | 'landing_page_two'

type Props = {
  prefix: Prefix
  title?: string
}

const FIELD_HELP_TEXTS: Record<Prefix, {
  imageContext: string
  heading: string
  description?: string
  cta?: string
}> = {
  landing_page_one: {
    imageContext: 'Onboarding page one',
    heading: 'Enter the main title displayed on the first onboarding landing page.',
  },
  landing_page_two: {
    imageContext: 'Onboarding page two',
    heading: 'Enter the main title displayed on the second onboarding landing page.',
    description: 'Add supporting copy displayed below the heading.',
    cta: 'Enter the call-to-action text shown on the second onboarding page.',
  },
}

const OnboardingLandingPageSettings = ({ prefix, title }: Props) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<any>()

  const watchedImage = useWatch({ control, name: `${prefix}.image` })
  const imagePreview = useObjectUrl(watchedImage)

  const helpText = FIELD_HELP_TEXTS[prefix]

  return (
    <div className="space-y-4 flex-1 flex flex-col">
      <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
        {title}
      </h5>
      <div className="flex flex-col gap-6">
        <FileInputUploadFieldNew
          label="Image"
          name={`${prefix}.image`}
          control={control}
          errors={errors}
          preview={getImageSrc(imagePreview)}
          allowedTypes={['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp']}
          required
          rules={{
            required: 'Image is required',
          }}
          tooltip={{
            title: 'Image requirements',
            items: [
              `Upload the image displayed on the ${helpText.imageContext}`,
              'JPG, PNG, WEBP, GIF, JPEG',
              '≤ 1 MB',
            ],
            position: 'top',
          }}
        />

        <InputField
          label="Heading"
          isTextarea={prefix === 'landing_page_one'}
          row={2}
          required
          helpText={helpText.heading}
          maxLength={150}
          register={register(`${prefix}.heading`, {
            required: 'Heading is required',
            maxLength: {
              value: 150,
              message: 'Heading must be 150 characters or less',
            },
          })}
          error={(errors[prefix] as any)?.heading?.message as string}
        />

        {prefix === 'landing_page_two' && (
          <>
            <InputField
              label="Description"
              isTextarea
              row={3}
              required
              helpText={helpText.description}
              maxLength={300}
              register={register(`${prefix}.description`, {
                required: 'Description is required',
                maxLength: {
                  value: 300,
                  message: 'Description must be 300 characters or less',
                },
              })}
              error={(errors[prefix] as any)?.description?.message as string}
            />

            <InputField
              label="CTA Label"
              type="text"
              required
              helpText={helpText.cta}
              maxLength={50}
              register={register(`${prefix}.cta_label`, {
                required: 'CTA label is required',
                maxLength: {
                  value: 50,
                  message: 'CTA label must be 50 characters or less',
                },
              })}
              error={(errors[prefix] as any)?.cta_label?.message as string}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default OnboardingLandingPageSettings
